import { ProductGroupStore } from './products/ProductGroupStore';
import { ContactGroupStore } from './contacts/ContactGroupStore';
import { JournalStore } from './warehousing/JournalStore';
import { ProductEntryStore } from './warehousing/ProductEntryStore';
import { LocationStore } from './warehousing/LocationStore';
import { WarehouseStore } from './warehousing/WarehouseStore';
import { IntegrationStore } from './IntegrationStore';
import { CreditNoteStore } from './CreditNoteStore';
import { CreditNoteItemStore } from './CreditNoteItemStore';
import { CurrencyStore } from './CurrencyStore';
import { VatRateStore } from './VatRateStore';
import Axios from 'axios';
import { Instance, onSnapshot, types, getEnv } from 'mobx-state-tree';
import { createContext, useContext } from 'react';
import { AxiosInstance } from 'axios';
import { AuthStore } from './AuthStore';
import { OrganisationStore } from './OrganisationStore';
import { TeamMemberStore } from './TeamMemberStore';
import { CompanyStore } from './contacts/CompanyStore';
import { ContactStore } from './contacts/ContactStore';
import {
  BOMItemStore,
  ProductStore,
  SupplierItemStore,
} from './products/ProductStore';
import { SaleStore } from './SaleStore';
import { SaleItemStore } from './SaleItemStore';
import { AgreementStore } from './AgreementStore';
import { PurchaseOrderStore } from './PurchaseOrderStore';
import { PurchaseOrderItemStore } from './PurchaseOrderItemStore';
import { RelationStore } from './contacts/RelationStore';
import { TaskLogStore, TaskStore } from './TaskStore';
import { BankDetailsStore } from './BankDetailsStore';
import config from '../config';
import { InvitationStore } from './InvitationStore';

const axios = Axios.create({
  baseURL: config.apiBaseUrl,
});

export interface IRootEnv {
  api: AxiosInstance;
  limit: number;
  language: string;
}

const env: IRootEnv = {
  api: axios,
  limit: 25,
  language: 'da',
};

export const RootModel = types
  .model('Root', {
    // Place any other stores here

    authStore: types.optional(AuthStore, {}),
    vatRateStore: types.optional(VatRateStore, {}),
    currencyStore: types.optional(CurrencyStore, {}),

    organisationStore: types.optional(OrganisationStore, {}),
    teamMemberStore: types.optional(TeamMemberStore, {}),
    invitationStore: types.optional(InvitationStore, {}),
    bankDetailsStore: types.optional(BankDetailsStore, {}),
    integrationStore: types.optional(IntegrationStore, {}),

    companyStore: types.optional(CompanyStore, {}),
    contactStore: types.optional(ContactStore, {}),
    relationStore: types.optional(RelationStore, {}),
    contactGroupStore: types.optional(ContactGroupStore, {}),

    productStore: types.optional(ProductStore, {}),
    productGroupStore: types.optional(ProductGroupStore, {}),
    bomItemStore: types.optional(BOMItemStore, {}),
    supplierItemStore: types.optional(SupplierItemStore, {}),

    saleStore: types.optional(SaleStore, {}),
    saleItemStore: types.optional(SaleItemStore, {}),

    creditNoteStore: types.optional(CreditNoteStore, {}),
    creditNoteItemStore: types.optional(CreditNoteItemStore, {}),

    agreementStore: types.optional(AgreementStore, {}),

    purchaseOrderStore: types.optional(PurchaseOrderStore, {}),
    purchaseOrderItemStore: types.optional(PurchaseOrderItemStore, {}),

    taskStore: types.optional(TaskStore, {}),
    taskLogStore: types.optional(TaskLogStore, {}),

    warehouseStore: types.optional(WarehouseStore, {}),
    locationStore: types.optional(LocationStore, {}),
    journalStore: types.optional(JournalStore, {}),
    productEntryStore: types.optional(ProductEntryStore, {}),
  })
  .actions((self) => ({
    afterCreate() {
      // Apply token to requests (if it exists on the authStore)
      const env = getEnv<IRootEnv>(self);
      env.api.interceptors.request.use((config) => {
        const token = self.authStore.authToken;
        if (token) {
          config.headers.Authorization = `Token ${token}`;
        }
        return config;
      });
      env.api.interceptors.response.use(
        (response) => response,
        (error) => {
          // When we are notified that we have no subscription, route to upgrade screen.
          if (
            error.response &&
            error.response.status === 403 &&
            error.response.data &&
            error.response.data.subscription_required
          ) {
            window.location.assign('/settings/subscription?upgrade=true');
          }
          return Promise.reject(error);
        }
      );
    },
    reset(soft?: boolean) {
      if (!soft) {
        localStorage.clear();
      }
      window.location.replace('/');
    },
  }));

// Initialize...
let initialState = RootModel.create({}, env);

const data = localStorage.getItem('authState');
if (data) {
  const json = JSON.parse(data);
  if (AuthStore.is(json)) {
    initialState = RootModel.create({ authStore: json as any }, env);
  }
}

export const rootStore = initialState;

onSnapshot(rootStore.authStore, (snapshot) => {
  // Only save authStore - we do not want to save other changes...
  localStorage.setItem('authState', JSON.stringify(snapshot));
});

export type RootInstance = Instance<typeof RootModel>;
const RootStoreContext = createContext<null | RootInstance>(null);

export const RootProvider = RootStoreContext.Provider;
export function useStores(): RootModelType {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('Store must not be null. Please add a context provider');
  }
  return store;
}

export type RootModelType = {
  reset: (soft?: boolean) => void;

  authStore: Instance<typeof AuthStore>;
  vatRateStore: Instance<typeof VatRateStore>;
  currencyStore: Instance<typeof CurrencyStore>;

  organisationStore: Instance<typeof OrganisationStore>;
  teamMemberStore: Instance<typeof TeamMemberStore>;
  invitationStore: Instance<typeof InvitationStore>;
  bankDetailsStore: Instance<typeof BankDetailsStore>;
  integrationStore: Instance<typeof IntegrationStore>;

  // Contacts
  contactStore: Instance<typeof ContactStore>;
  companyStore: Instance<typeof CompanyStore>;
  relationStore: Instance<typeof RelationStore>;
  contactGroupStore: Instance<typeof ContactGroupStore>;

  // Products
  productStore: Instance<typeof ProductStore>;
  productGroupStore: Instance<typeof ProductGroupStore>;
  bomItemStore: Instance<typeof BOMItemStore>;
  supplierItemStore: Instance<typeof SupplierItemStore>;

  // Sales
  saleStore: Instance<typeof SaleStore>;
  saleItemStore: Instance<typeof SaleItemStore>;

  creditNoteStore: Instance<typeof CreditNoteStore>;
  creditNoteItemStore: Instance<typeof CreditNoteItemStore>;

  agreementStore: Instance<typeof AgreementStore>;

  // Purchasing
  purchaseOrderStore: Instance<typeof PurchaseOrderStore>;
  purchaseOrderItemStore: Instance<typeof PurchaseOrderItemStore>;

  // Tasks
  taskStore: Instance<typeof TaskStore>;
  taskLogStore: Instance<typeof TaskLogStore>;

  // Warehousing
  warehouseStore: Instance<typeof WarehouseStore>;
  locationStore: Instance<typeof LocationStore>;
  journalStore: Instance<typeof JournalStore>;
  productEntryStore: Instance<typeof ProductEntryStore>;
};
